import React, { useState, useEffect } from 'react'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'

function SsisJob(props) {
    const [isLoadingRequest, setIsLoadingRequest] = useState(false)
    const [sqLDbList, setSqLDbList] = useState([])
    const [ssisPackages, setSsisPackages] = useState([])
    const [databaseId, setDatabaseId] = useState(-1)
    const [databaseName, setDatabaseName] = useState('')
    const [jobFiles, setJobFiles] = useState([])
    const [selectedJobFileId, setSelectedJobFileId] = useState('')
    const [selectedJobFile, setSelectedJobFile] = useState('')
    const [selectedSsisJobId, setSelectedSsisJobId] = useState(-1)

    // trigger on component mount
    useEffect(() => {
        if (sqLDbList.length === 0) {
            let dBList = props.databases.filter(x => x.serverType == 0)
            setSqLDbList(dBList)
        }

        if (ssisPackages.length === 0) {
            setSsisPackages(props.ssisPackages)
        }

        // This is true if we are in edit mode
        if (props.taskData.databaseId !== -1) {
            setDatabaseId(props.taskData.databaseId)
            setDatabaseName(props.taskData.databaseName)
            setIsLoadingRequest(false)
        }

        if (props.jobFiles && props.jobFiles.length > 0) {
            setJobFiles(props.jobFiles)
            setSelectedJobFileId(props.taskData.selectedZipFileId)
            setSelectedJobFile(props.taskData.selectedZipFile)
            zipFileSelectedWithFiles(props.taskData.selectedZipFile, props.jobFiles)
        }

        if (props.taskData.ssisJobId) {
            setSelectedSsisJobId(props.taskData.ssisJobId)
        }
    }, [])

    function handleDatabaseChange(event) {
        event.preventDefault()
        setIsLoadingRequest(false)
        setDatabaseId(event.target.value)

        let lookupDatabaseName = ""
        sqLDbList.forEach(db => {
            if (db.id === event.target.value) {
                lookupDatabaseName = db.dBname
                setDatabaseName(db.dBname)
            }
        })
        fireOnSaveEvent(event.target.value, lookupDatabaseName, selectedJobFileId, selectedJobFile, selectedSsisJobId)
    }
    
    function handleZipFileChange(event) {
        event.preventDefault()
        zipFileSelected(event.target.value)
    }

    function zipFileSelected(zipFileName) {
        setIsLoadingRequest(false)
        setSelectedJobFileId(zipFileName)
        setSelectedJobFile('')

        let lookup = null
        for (let x = 0; x < jobFiles.length; x++) {
            if (jobFiles[x]["identifier"] === zipFileName) {
                lookup = jobFiles[x]
            }
        }

        if (lookup) { 
            setSelectedJobFile(lookup["name"])
            fireOnSaveEvent(databaseId, databaseName, zipFileName, lookup["name"], selectedSsisJobId)
        } else {
            setSelectedJobFile('')
        }
    }

    function zipFileSelectedWithFiles(zipFileName, files) {
        setIsLoadingRequest(false)
        setSelectedJobFileId(zipFileName)
        setSelectedJobFile('')

        let lookup = null
        for (let x = 0; x < jobFiles.length; x++) {
            if (jobFiles[x]["identifier"] === zipFileName) {
                lookup = jobFiles[x]
            }
        }

        if (lookup) { 
            setSelectedJobFile(jobFiles[x]["name"])
            fireOnSaveEvent(databaseId, databaseName, zipFileName, jobFiles[x]["name"], selectedSsisJobId)
        } else {
            setSelectedJobFile('')
        }
    }

    /**
     * fireOnSaveEvent
     * * If component has a prop for the [onSave] event, the purpose of this is to fire that event with the latest information
     * @param {int} dbId - Selected database ID
     * @param {string} dbName - Selected name of the database
     * @param {string} zipFileId - Selected zip file unique ID
     * @param {string} zipFileName - Job file name attached to SSIS job to run
     * @param {int} intergratedJobId - SSIS job ID
     */
    function fireOnSaveEvent(dbId, dbName, zipFileId, zipFileName, intergratedJobId) {
        if (props.onSave) {
            props.onSave({
                "databaseId": dbId,
                "databaseName": dbName,
                "dataSourceSystemName": "",
                "dataSourceName": "",
                "membersToRemove": [],
                "removeFromSegmentation": false,
                "removeFromMasking": false,
                "selectedDimensionSystemName": "",
                "selectedZipFileId": zipFileId,
                "selectedZipFile": zipFileName,
                "selectedFileWithinZip": "",
                "selectedSourceColumn": "",
                "selectedTargetColumn": "",
                "rawSql": "",
                "ssisJobId": intergratedJobId,
                "wizardFile": "",
                "wizardTextQualifier": 0,
                "sourceFiles": [],
                "originDatabaseId": -1,
                "originDatabaseDataSourceSystemName": "",
                "appendDimensionInfo": [],
                "doNotImportFacts": false,
                "getFactsAllMembers": true,
                "getFactsForSpecificMembers": false,
                "transformSelections": [],
                "delimiter": 0,
                "customDelimiter": "",
                "excelDataTable": "",
                "excelDataTables": [],
                "importFromExistingMembers": false,
                "segmentationDeclaration": [],
                "segmentationAssignments": [] })
        }
    }

    function renderSsisJobsDropdown() {
        if (ssisPackages.length > 0) {
            return (
                <FormControl className="field" fullWidth>
                    <InputLabel id="select-ssis-job-label">Select SSIS Job</InputLabel>
                    <Select
                        labelId="select-ssis-job-label"
                        id="select-ssis-job"
                        label="Select SSIS Job"
                        onChange={(e) => {
                            e.preventDefault()
                            setSelectedSsisJobId(e.target.value)
                            fireOnSaveEvent(databaseId, databaseName, selectedJobFileId, selectedJobFile, e.target.value)
                        }}
                        defaultValue={0}
                        value={selectedSsisJobId}
                        required
                        fullWidth>
                            <MenuItem value={-1}>Select SSIS Job</MenuItem>
                            { ssisPackages.map((x) => x && <MenuItem key={x.id} value={x.id}>{x.jobName}</MenuItem>)}
                    </Select>
                </FormControl>
            )
        }
        return null
    }

    return <Paper elevation={3} className="dbFunctionsPaper">
        <h3>Run SSIS Job</h3>
        <FormControl className="field" fullWidth>
            <InputLabel id="select-databse-label">Select Database</InputLabel>
            <Select
                labelId="select-databse-label"
                id="select-databse"
                label="Select Database"
                onChange={handleDatabaseChange}
                defaultValue={sqLDbList.length > 0 ? sqLDbList[0].id : -1}
                value={databaseId}
                required
                fullWidth
                disabled={isLoadingRequest}>
                    <MenuItem value={-1}>Select Database</MenuItem>
                    { sqLDbList.map((x) => x && <MenuItem key={x.id} value={x.id}>{x.dBname}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl className="field" fullWidth>
            <InputLabel id="select-zip-file-label">Select Zip File</InputLabel>
            <Select
                labelId="select-zip-file-label"
                id="select-zip-file"
                label="Select Zip File"
                onChange={handleZipFileChange}
                value={selectedJobFileId}
                defaultValue={''}
                required
                fullWidth
                disabled={isLoadingRequest}>
                    <MenuItem value={''}>Select Zip File</MenuItem>
                    { jobFiles.map((x) => x && !x.name.toLowerCase().includes(".zip") && <MenuItem key={x.identifier} value={x.identifier}>{x.name}</MenuItem>) }
            </Select>
        </FormControl>
        {renderSsisJobsDropdown()}
    </Paper>
}

export default SsisJob